/**
 * True when the app is served from a local dev host (Vite, CRA, etc.).
 */
const isLocalDevHost = () => {
  if (typeof window === 'undefined' || !window.location?.hostname) {
    return process.env.NODE_ENV !== 'production';
  }
  const host = window.location.hostname;
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '[::1]' ||
    host.endsWith('.local')
  );
};

/**
 * Normalize port for comparison (URL may omit default 80/443).
 */
const urlPortKey = (u) => {
  if (u.port) return u.port;
  return u.protocol === 'https:' ? '443' : '80';
};

/**
 * Canonical origin without `www.` (apex). Uploads and API should match this host in production.
 */
export const getCanonicalSiteOrigin = () => {
  if (typeof window === 'undefined' || !window.location?.origin) {
    return '';
  }
  try {
    const u = new URL(window.location.origin);
    if (u.hostname.startsWith('www.')) {
      u.hostname = u.hostname.slice(4);
    }
    return u.origin;
  } catch {
    return window.location.origin;
  }
};

/**
 * API base for axios and URL helpers.
 * Same-host matches use canonical (non-www) origin + `/api`.
 * Local dev keeps distinct ports (e.g. :5173 vs :5000) by using the env URL as-is.
 */
export const resolveApiBaseURL = () => {
  // Local dev always uses the Vite proxy (/api -> localhost:5000), even if .env points at production.
  if (isLocalDevHost()) {
    return '/api';
  }

  const env = process.env.REACT_APP_API_URL;
  const envTrim = env && String(env).trim();

  if (typeof window === 'undefined' || !window.location?.origin) {
    if (process.env.NODE_ENV === 'production' && !envTrim) {
      return '/api';
    }
    return envTrim ? envTrim.replace(/\/+$/, '') : '/api';
  }

  const pageOrigin = window.location.origin;
  const canonicalOrigin = getCanonicalSiteOrigin() || pageOrigin;
  if (!envTrim) {
    return `${canonicalOrigin}/api`.replace(/\/+$/, '');
  }

  try {
    const baseForParse = envTrim.endsWith('/api') ? envTrim : `${envTrim}/api`;
    const envUrl = new URL(baseForParse);
    const pageUrl = new URL(pageOrigin);
    const envHost = envUrl.hostname.replace(/^www\./, '');
    const pageHost = pageUrl.hostname.replace(/^www\./, '');
    if (envHost === pageHost && urlPortKey(envUrl) === urlPortKey(pageUrl)) {
      return `${canonicalOrigin}/api`.replace(/\/+$/, '');
    }
  } catch {
    // invalid REACT_APP_API_URL
  }

  return envTrim.replace(/\/+$/, '');
};

/**
 * Get the API base URL consistently across the application
 */
export const getApiBaseUrl = () => resolveApiBaseURL();

/**
 * Get a direct backend API base URL for multipart/file uploads.
 * In production: uses REACT_APP_API_URL from env (set in .env / env.example).
 * In development: uses REACT_APP_API_URL if set, else localhost:5000 to bypass CRA proxy (which can strip multipart body).
 * Use with axios: api.post(path, formData, { baseURL: getDirectApiBaseUrl(), ...headers })
 */
export const getDirectApiBaseUrl = () => {
  // Multipart uploads must hit the backend port directly (Vite proxy can strip bodies).
  if (isLocalDevHost()) {
    return 'http://localhost:5000/api';
  }
  if (process.env.NODE_ENV !== 'production' && typeof window !== 'undefined') {
    if (!process.env.REACT_APP_API_URL) {
      return 'http://localhost:5000/api';
    }
  }
  return resolveApiBaseURL();
};

/**
 * Get the base URL for product images (backend serves at /api/images/:productId/:assetId)
 */
export const getImageBaseUrl = () => {
  const apiBaseUrl = getApiBaseUrl();
  // If using relative URLs, use /api/images
  if (apiBaseUrl === '/api' || apiBaseUrl.startsWith('/')) {
    return '/api/images';
  }
  // If baseUrl already ends with /api (e.g. https://polyassetvault.com/api), append /images only
  if (apiBaseUrl.endsWith('/api')) {
    return `${apiBaseUrl}/images`;
  }
  return `${apiBaseUrl}/api/images`;
};

/**
 * Construct a full URL for an API resource
 * @param {string} path - The API path (e.g., '/api/user/profile-pictures/image.jpg')
 * @returns {string} - The full URL
 */
export const getApiUrl = (path) => {
  if (!path) {
    return '';
  }
  
  // If path already starts with http, return as-is
  if (path.startsWith('http')) {
    return path;
  }
  
  const baseUrl = getApiBaseUrl();
  
  // If path already starts with /api, handle it carefully
  if (path.startsWith('/api')) {
    // If baseUrl already ends with /api, remove the duplicate /api from path
    if (baseUrl.endsWith('/api')) {
      return `${baseUrl}${path.substring(4)}`; // Remove '/api' from path
    }
    // Otherwise, just prepend baseUrl
    return `${baseUrl}${path}`;
  }
  
  // CRITICAL FIX: If path doesn't start with /api, we need to prepend it
  // This ensures paths like /products/... become /api/products/...
  if (baseUrl === '/api' || baseUrl.endsWith('/api')) {
    // If baseUrl is /api, prepend /api to the path
    return `/api${path.startsWith('/') ? path : '/' + path}`;
  }
  
  // Otherwise, construct the full path with baseUrl
  return `${baseUrl}${path.startsWith('/') ? path : '/' + path}`;
};

/**
 * Get URL for uploads (e.g. /uploads/...) - backend serves these at /uploads, not under /api
 * @param {string} path - Path like /uploads/filename
 * @returns {string} - Full URL to the upload
 */
export const getUploadsUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const baseUrl = getApiBaseUrl();
  const canonical = typeof window !== 'undefined' ? getCanonicalSiteOrigin() : '';
  const uploadsOrigin = baseUrl === '/api' || baseUrl.endsWith('/api')
    ? canonical || (typeof window !== 'undefined' ? window.location.origin : '')
    : baseUrl.replace(/\/api\/?$/, '');
  // If derived from absolute API base, strip www from origin for consistency
  let origin = uploadsOrigin;
  if (origin && typeof window !== 'undefined' && canonical) {
    try {
      const o = new URL(origin);
      if (o.hostname.startsWith('www.')) {
        o.hostname = o.hostname.slice(4);
        origin = o.origin;
      }
    } catch {
      // keep origin
    }
  }
  const finalOrigin = origin || (typeof window !== 'undefined' ? canonical || window.location.origin : '');
  return `${finalOrigin}${normalized}`;
};

/**
 * Portfolio / post media: uploads vs API paths; normalize `uploads/...` without leading slash;
 * map legacy `/api/uploads/...` to real `/uploads/...`.
 */
export const getMediaUrl = (inputPath) => {
  if (!inputPath) return '';
  const raw = String(inputPath).trim();
  if (!raw) return '';
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;

  const normalized = raw.startsWith('/') ? raw : `/${raw}`;
  if (normalized.startsWith('/uploads/') || normalized === '/uploads') {
    return getUploadsUrl(normalized);
  }
  if (normalized.startsWith('/api/uploads/')) {
    return getUploadsUrl(normalized.replace(/^\/api/, '') || '/');
  }
  if (normalized.startsWith('/api/')) {
    return getApiUrl(normalized);
  }
  if (raw.startsWith('api/')) {
    return getApiUrl(`/${raw}`);
  }
  return getApiUrl(normalized);
};

export const getWebSocketBaseUrl = () => {
  if (typeof window === 'undefined') {
    return '';
  }

  const apiBaseUrl = getApiBaseUrl();
  if (apiBaseUrl === '/api' || apiBaseUrl.startsWith('/')) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const co = getCanonicalSiteOrigin();
    if (co) {
      try {
        const u = new URL(co);
        return `${protocol}//${u.host}`;
      } catch {
        // fall through
      }
    }
    return `${protocol}//${window.location.host}`;
  }

  return apiBaseUrl
    .replace(/\/api\/?$/, '')
    .replace(/^http:/, 'ws:')
    .replace(/^https:/, 'wss:');
};

export const getWebSocketUrl = (path = '') => {
  const baseUrl = getWebSocketBaseUrl();
  if (!baseUrl) {
    return '';
  }
  return `${baseUrl}${path.startsWith('/') ? path : `/${path}`}`;
};

/**
 * Get image URL for product assets
 * @param {string} productId - Product ID
 * @param {string} assetId - Asset ID
 * @returns {string} - Full image URL
 */
export const getImageUrl = (productId, assetId) => {
  if (!productId || !assetId) {
    return '';
  }
  const baseUrl = getImageBaseUrl();
  return `${baseUrl}/${productId}/${assetId}`;
};
